import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { motion, useScroll, useTransform } from "framer-motion";
import { gsap } from "gsap";
import img from "../../assets/heroImg.png";

const Hero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.6], ["0px", "-60px"]);
  const rightY = useTransform(scrollYProgress, [0, 1], ["0px", "80px"]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".h-eyebrow", {
        y: 20,
        opacity: 0,
        duration: 0.9,
        delay: 0.3,
        ease: "power3.out",
      });
      gsap.from(".h-line", {
        y: 140,
        opacity: 0,
        duration: 1.5,
        stagger: 0.13,
        ease: "power4.out",
        delay: 0.5,
      });
      gsap.from(".h-sub", {
        y: 24,
        opacity: 0,
        duration: 1,
        delay: 1.2,
        ease: "power3.out",
      });
      gsap.from(".h-cta", {
        y: 18,
        opacity: 0,
        duration: 0.9,
        delay: 1.5,
        ease: "power3.out",
      });
      gsap.from(".h-stat", {
        y: 30,
        opacity: 0,
        duration: 0.8,
        stagger: 0.08,
        delay: 1.6,
        ease: "power3.out",
      });
      gsap.from(".h-frame", {
        scale: 0.94,
        opacity: 0,
        duration: 1.4,
        delay: 0.6,
        ease: "power4.out",
      });
      gsap.from(".h-pill", {
        y: 20,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        delay: 1.2,
        ease: "power3.out",
      });
      gsap.from(".h-deco-line", {
        scaleX: 0,
        duration: 1.2,
        stagger: 0.15,
        delay: 0.8,
        ease: "power4.inOut",
        transformOrigin: "left center",
      });
      gsap.from(".h-bracket", {
        opacity: 0,
        scale: 0.7,
        duration: 0.8,
        stagger: 0.1,
        delay: 1.0,
        ease: "back.out(1.7)",
      });
      gsap.from(".h-scroll", {
        opacity: 0,
        duration: 1,
        delay: 2.2,
        ease: "power2.out",
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  // Updated pills to match actual services
  const pills = [
    "Deep Cleansing",
    "HydraFacial",
    "Dermapen",
    "Ultra Contour",
    "Maderoterapia",
  ];

  return (
    <Section ref={containerRef} id="hero">
      <BgMesh style={{ y: bgY }}>
        <Orb
          $top="5%"
          $left="55%"
          $size="900px"
          $color="rgba(196,149,122,0.11)"
        />
        <Orb
          $top="65%"
          $left="20%"
          $size="600px"
          $color="rgba(201,169,110,0.07)"
        />
        <Orb
          $top="30%"
          $left="85%"
          $size="400px"
          $color="rgba(196,149,122,0.08)"
        />
        <GridLines />
        <DiagLine $rotate="25deg" $top="0" $left="30%" />
        <DiagLine $rotate="-18deg" $top="40%" $left="70%" />
      </BgMesh>

      <Layout>
        {/* LEFT */}
        <LeftCol
          as={motion.div}
          style={{ opacity: contentOpacity, y: contentY }}
        >
          <Eyebrow className="h-eyebrow">
            <EyeDash className="h-deco-line" />
            <EyeLabel>Elaia Aesthetic</EyeLabel>
            <EyeBadge>Est. 2026</EyeBadge>
          </Eyebrow>

          <TitleBlock>
            <TitleLine>
              <Title className="h-line">Your skin deserves</Title>
            </TitleLine>
            <TitleLine>
              <TitleAccent className="h-line">the good </TitleAccent>
            </TitleLine>
            <TitleLine $last>
              <TitleOutline className="h-line">stuff.</TitleOutline>
            </TitleLine>
          </TitleBlock>

          <SubRow className="h-sub">
            <SubDivider />
            <Sub>
              Trajtime estetike premium për ata që kërkojnë përsosmëri në çdo
              detaj — duke përdorur vetëm teknologjinë më të avancuar klinike.
            </Sub>
          </SubRow>

          <CtaRow className="h-cta">
            <PrimaryBtn
              as={motion.a}
              href="#services"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              <BtnInner>Shiko Shërbimet</BtnInner>
            </PrimaryBtn>
            <GhostBtn
              as={motion.a}
              href="#contact"
              whileHover={{ borderColor: "var(--rose)", color: "var(--rose)" }}
            >
              Rezervo Termin
            </GhostBtn>
          </CtaRow>

          <Stats>
            {[
              { val: "5+", label: "Vite Eksperiencë" },
              { val: "8+", label: "Trajtime" },
              { val: "99%", label: "Kënaqësi" },
            ].map((s) => (
              <Stat key={s.label} className="h-stat">
                <StatVal>{s.val}</StatVal>
                <StatLabel>{s.label}</StatLabel>
              </Stat>
            ))}
          </Stats>
        </LeftCol>

        {/* RIGHT */}
        <RightCol as={motion.div} style={{ y: rightY }}>
          <Frame className="h-frame">
            <FrameInner>
              <Corner $pos="tl" className="h-bracket" />
              <Corner $pos="tr" className="h-bracket" />
              <Corner $pos="bl" className="h-bracket" />
              <Corner $pos="br" className="h-bracket" />
              <FrameContent>
                <FrameIcon>✦</FrameIcon>
                <FramePlaceholder>
                  <img src={img} alt="" />
                </FramePlaceholder>
              </FrameContent>
              <FrameGlow />
            </FrameInner>

            <AccentBox
              as={motion.div}
              animate={{ y: [0, -8, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
            >
              <AccentBoxLabel>Trajtimi i Muajit</AccentBoxLabel>
              <AccentBoxVal>HydraFacial</AccentBoxVal>
              <AccentBoxSub>Pastrim · Eksfolim · Hidratim</AccentBoxSub>
            </AccentBox>

            <RatingBadge
              as={motion.div}
              animate={{ y: [0, 6, 0] }}
              transition={{
                repeat: Infinity,
                duration: 6,
                ease: "easeInOut",
                delay: 1,
              }}
            >
              <RatingStars>★★★★★</RatingStars>
              <RatingText>Shërbime Premium</RatingText>
            </RatingBadge>
          </Frame>

          <PillsRow>
            {pills.map((p) => (
              <Pill
                key={p}
                className="h-pill"
                as={motion.span}
                whileHover={{
                  scale: 1.05,
                  borderColor: "var(--rose)",
                  color: "var(--rose)",
                }}
              >
                {p}
              </Pill>
            ))}
          </PillsRow>
        </RightCol>
      </Layout>

      <HRule className="h-deco-line" />
      <ScrollHint className="h-scroll">
        <ScrollLine
          as={motion.div}
          animate={{ scaleY: [0.3, 1, 0.3] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        />
        <ScrollLabel>Scroll</ScrollLabel>
      </ScrollHint>
      <SideLabel>Premium Aesthetic Clinic</SideLabel>
      <Watermark>01</Watermark>
    </Section>
  );
};

/* ── Keyframes ── */
const floatSlow = keyframes`0%, 100% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-14px) rotate(1deg); }`;
const gridPulse = keyframes`0%, 100% { opacity: 0.03; } 50% { opacity: 0.06; }`;
const shimmer = keyframes`0% { background-position: -200% center; } 100% { background-position: 200% center; }`;

/* ── Styles (identical to original, no layout changes) ── */
const Section = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: var(--charcoal);
  padding-top: 100px;
`;
const BgMesh = styled(motion.div)`
  position: absolute;
  inset: -20%;
  z-index: 0;
  pointer-events: none;
`;
const Orb = styled.div`
  position: absolute;
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  background: radial-gradient(
    circle,
    ${({ $color }) => $color} 0%,
    transparent 65%
  );
  transform: translate(-50%, -50%);
  border-radius: 50%;
`;
const GridLines = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(196, 149, 122, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(196, 149, 122, 0.04) 1px, transparent 1px);
  background-size: 80px 80px;
  animation: ${gridPulse} 6s ease-in-out infinite;
`;
const DiagLine = styled.div`
  position: absolute;
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
  width: 600px;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(196, 149, 122, 0.12),
    transparent
  );
  transform: rotate(${({ $rotate }) => $rotate});
`;
const Layout = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 60px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  min-height: calc(100vh - 100px);
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    padding: 0 32px;
    gap: 60px;
  }
  @media (max-width: 768px) {
    padding: 0 24px;
  }
`;
const LeftCol = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0;
  @media (max-width: 500px) {
    justify-content: center;
    align-items: center;
    text-align: center;
  }
`;
const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 36px;
`;
const EyeDash = styled.div`
  width: 36px;
  height: 1px;
  background: var(--rose);
  transform-origin: left center;
`;
const EyeLabel = styled.span`
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--rose);
`;
const EyeBadge = styled.span`
  font-family: var(--font-body);
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--charcoal);
  background: var(--rose);
  padding: 3px 10px;
  opacity: 0.8;
`;
const TitleBlock = styled.div`
  margin-bottom: 36px;
`;
const TitleLine = styled.div`
  overflow: visible;
  line-height: ${({ $last }) => ($last ? "1" : "0.95")};
`;
const Title = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(60px, 8.5vw, 128px);
  font-weight: 300;
  color: var(--cream);
  letter-spacing: -0.03em;
  line-height: inherit;
  margin: 0;
  display: block;
`;
const TitleAccent = styled(Title)`
  font-style: italic;
  color: var(--rose);
`;
const TitleOutline = styled(Title)`
  -webkit-text-stroke: 1.5px rgb(216, 197, 166);
  color: transparent;
  font-style: italic;
`;
const SubRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 48px;
`;
const SubDivider = styled.div`
  width: 2px;
  min-height: 80px;
  background: linear-gradient(to bottom, var(--rose), transparent);
  flex-shrink: 0;
  margin-top: 4px;
`;
const Sub = styled.p`
  color: var(--taupe);
  font-family: var(--font-body);
  font-size: clamp(14px, 1.4vw, 16px);
  font-weight: 300;
  line-height: 1.85;
  letter-spacing: 0.02em;
  margin: 0;
  max-width: 400px;
`;
const CtaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 52px;
  @media (max-width: 500px) {
    justify-content: center;
  }
`;
const PrimaryBtn = styled.a`
  display: inline-block;
  text-decoration: none;
  color: var(--charcoal);
  background: var(--rose);
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  padding: 16px 40px;
  transition: background 0.3s;
  position: relative;
  overflow: hidden;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.15) 50%,
      transparent 100%
    );
    background-size: 200% auto;
    animation: ${shimmer} 3s linear infinite;
    opacity: 0;
    transition: opacity 0.3s;
  }
  &:hover::after {
    opacity: 1;
  }
`;
const BtnInner = styled.span`
  position: relative;
  z-index: 1;
`;
const GhostBtn = styled.a`
  display: inline-block;
  text-decoration: none;
  color: var(--cream);
  border: 1px solid rgba(245, 240, 232, 0.2);
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  padding: 16px 36px;
  transition:
    border-color 0.3s,
    color 0.3s;
`;
const Stats = styled.div`
  display: flex;
  gap: 0;
`;
const Stat = styled.div`
  padding: 20px 28px 20px 0;
  border-right: 1px solid rgba(196, 149, 122, 0.12);
  margin-right: 28px;
  &:last-child {
    border-right: none;
    margin-right: 0;
  }
`;
const StatVal = styled.div`
  font-family: var(--font-display);
  font-size: clamp(28px, 3vw, 40px);
  font-weight: 300;
  color: var(--rose);
  letter-spacing: -0.02em;
  line-height: 1;
  margin-bottom: 4px;
`;
const StatLabel = styled.div`
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--taupe);
`;
const RightCol = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  @media (max-width: 1024px) {
    display: none;
  }
`;
const Frame = styled.div`
  position: relative;
  width: 100%;
  max-width: 520px;
`;
const FrameInner = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3/4;
  background: linear-gradient(
    145deg,
    rgba(44, 40, 37, 0.9) 0%,
    rgba(26, 23, 20, 0.95) 60%,
    rgba(44, 36, 32, 0.85) 100%
  );
  border: 1px solid rgba(196, 149, 122, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  animation: ${floatSlow} 8s ease-in-out infinite;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        ellipse at 30% 20%,
        rgba(196, 149, 122, 0.12) 0%,
        transparent 60%
      ),
      radial-gradient(
        ellipse at 80% 80%,
        rgba(201, 169, 110, 0.08) 0%,
        transparent 50%
      );
  }
`;
const Corner = styled.div`
  position: absolute;
  width: 24px;
  height: 24px;
  border-color: var(--rose);
  border-style: solid;
  opacity: 0.6;
  z-index: 2;
`;
const FrameContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  z-index: 1;
`;
const FrameIcon = styled.div`
  font-size: 48px;
  color: var(--rose);
  opacity: 0.3;
`;
const FramePlaceholder = styled.p`
  font-family: var(--font-body);
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--taupe);
  text-align: center;
  line-height: 2;
  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`;
const FrameGlow = styled.div`
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 120px;
  background: radial-gradient(
    ellipse,
    rgba(196, 149, 122, 0.15) 0%,
    transparent 70%
  );
  pointer-events: none;
`;
const AccentBox = styled(motion.div)`
  position: absolute;
  left: -44px;
  bottom: 14%;
  background: rgba(230, 220, 211, 0.747);
  border: 1px solid rgba(196, 149, 122, 0.25);
  padding: 20px 24px;
  backdrop-filter: blur(12px);
  min-width: 180px;
`;
const AccentBoxLabel = styled.div`
  font-family: var(--font-body);
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--rose);
  margin-bottom: 8px;
`;
const AccentBoxVal = styled.div`
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 400;
  color: var(--cream);
  margin-bottom: 2px;
`;
const AccentBoxSub = styled.div`
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--taupe);
  letter-spacing: 0.05em;
`;
const RatingBadge = styled(motion.div)`
  position: absolute;
  right: -36px;
  top: 18%;
  background: rgba(233, 226, 221, 0.397);
  border: 1px solid rgba(196, 149, 122, 0.3);
  backdrop-filter: blur(16px);
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;
const RatingStars = styled.div`
  color: var(--gold);
  font-size: 13px;
  letter-spacing: 2px;
`;
const RatingText = styled.div`
  font-family: var(--font-body);
  font-size: 10px;
  letter-spacing: 0.12em;
  color: white;
  white-space: nowrap;
  opacity: 0.8;
`;
const PillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  max-width: 520px;
`;
const Pill = styled(motion.span)`
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  border: 1px solid rgba(196, 149, 122, 0.18);
  padding: 8px 18px;
  cursor: default;
  transition:
    color 0.3s,
    border-color 0.3s;
  background: rgba(26, 23, 20, 0.5);
  backdrop-filter: blur(8px);
`;
const HRule = styled.div`
  position: absolute;
  bottom: 80px;
  left: 60px;
  right: 60px;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(196, 149, 122, 0.2),
    transparent
  );
  transform-origin: left center;
`;
const ScrollHint = styled.div`
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  z-index: 2;
`;
const ScrollLine = styled(motion.div)`
  width: 1px;
  height: 52px;
  background: linear-gradient(
    to bottom,
    transparent,
    var(--rose) 60%,
    transparent
  );
  transform-origin: top center;
`;
const ScrollLabel = styled.span`
  font-family: var(--font-body);
  font-size: 9px;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: rgba(196, 149, 122, 0.5);
`;
const SideLabel = styled.div`
  position: absolute;
  left: -90px;
  top: 50%;
  transform: translateY(-50%) rotate(-90deg);
  font-family: var(--font-body);
  font-size: 9px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: rgba(196, 149, 122, 0.2);
  white-space: nowrap;
  z-index: 1;
  @media (max-width: 1200px) {
    display: none;
  }
`;
const Watermark = styled.div`
  position: absolute;
  right: 40px;
  bottom: 60px;
  font-family: var(--font-display);
  font-size: clamp(100px, 14vw, 200px);
  font-weight: 300;
  color: transparent;
  -webkit-text-stroke: 1px rgba(196, 149, 122, 0.06);
  line-height: 1;
  pointer-events: none;
  user-select: none;
  z-index: 0;
  letter-spacing: -0.05em;
  @media (max-width: 768px) {
    display: none;
  }
`;

export default Hero;
